import { AlertTriangle, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../utils/cn';
import { estadoMeta, formatRelative } from '../gpsMock';
import { useVehicleType } from '../vehicleTypes';
import { SignalBars } from './SignalBars';

const FILTERS = [
  { value: null,         label: 'Todos' },
  { value: 'movimiento', label: 'En movimiento' },
  { value: 'detenido',   label: 'Detenidos' },
  { value: 'inactivo',   label: 'Inactivos' },
  { value: 'sin_senal',  label: 'Sin señal' },
];

// estado → token de estado (paridad con KPI/Legend; el hex de gpsMock es solo
// para la capa Leaflet, no theme-able).
const ESTADO_DOT = {
  movimiento: 'bg-status-success',
  detenido:   'bg-status-info',
  inactivo:   'bg-status-warning',
  sin_senal:  'bg-status-neutral',
};

export function DeviceList({
  devices,
  live,
  selectedId,
  onSelect,
  search,
  onSearch,
  estadoFilter,
  onEstadoFilter,
  loading,
}) {
  const { t } = useTranslation();
  const counts = devices.reduce((acc, d) => {
    acc[d.estado] = (acc[d.estado] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full bg-bg border-r border-border">
      {/* Búsqueda */}
      <div className="p-3 border-b border-border space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={t("Patente, IMEI, alias o conductor…")}
            className="h-9 w-full rounded-lg border border-border bg-bg-muted pl-9 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          {search && (
            <button
              onClick={() => onSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg p-1 rounded-md"
              aria-label={t("Limpiar")}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {/* Chips de filtro por estado */}
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => {
            const active = estadoFilter === f.value;
            const count = f.value ? counts[f.value] ?? 0 : devices.length;
            return (
              <button
                key={String(f.value)}
                onClick={() => onEstadoFilter(f.value)}
                className={cn(
                  'inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-md transition-colors',
                  active ? 'bg-brand-500 text-white' : 'bg-bg-muted text-fg-muted hover:text-fg',
                )}
              >
                {f.value && (
                  <span
                    className={cn('w-1.5 h-1.5 rounded-full', ESTADO_DOT[f.value])}
                    aria-hidden
                  />
                )}
                {t(f.label)}
                <span
                  className={cn(
                    'tabular-nums text-[10px] px-1 rounded',
                    active ? 'bg-white/20' : 'bg-bg',
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lista */}
      <ul className="flex-1 overflow-y-auto divide-y divide-border">
        {loading
          ? Array.from({ length: 8 }).map((_, i) => (
              <li key={i} className="p-3">
                <div className="h-14 rounded-lg bg-bg-muted animate-pulse" />
              </li>
            ))
          : devices.length === 0
            ? <li className="p-8 text-center text-sm text-fg-muted">{t("Sin resultados.")}</li>
            : devices.map((d) => (
                <DeviceRow
                  key={d.id}
                  device={d}
                  live={live[d.id]}
                  selected={selectedId === d.id}
                  onClick={() => onSelect(d.id)}
                />
              ))}
      </ul>
    </div>
  );
}

function DeviceRow({ device, live, selected, onClick }) {
  const { t } = useTranslation();
  const meta = estadoMeta[device.estado];
  const vel = live?.velocidadKmh ?? device.velocidadKmh;
  const alarmas = device.alarmasActivas?.length ?? 0;
  const dim = device.estado === 'sin_senal' || device.patente === '— · —';
  const vehicleType = useVehicleType(device);
  const VehicleIcon = vehicleType.Lucide;

  return (
    <li>
      <button
        onClick={onClick}
        className={cn(
          'w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors group',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
          selected ? 'bg-brand-50' : 'hover:bg-bg-muted',
        )}
      >
        <div className="pt-1 relative shrink-0">
          <span
            className={cn('block w-2.5 h-2.5 rounded-full', ESTADO_DOT[device.estado])}
            style={{ boxShadow: '0 0 0 2px rgb(var(--bg))' }}
            aria-hidden
          />
          {device.estado === 'movimiento' && (
            <span
              className={cn('absolute inset-0 w-2.5 h-2.5 rounded-full opacity-50 animate-ping motion-reduce:hidden', ESTADO_DOT[device.estado])}
              aria-hidden
            />
          )}
        </div>
        <div className={cn('flex-1 min-w-0', dim && 'opacity-70')}>
          <div className="flex items-baseline justify-between gap-2">
            <span className={cn('font-mono text-[13px] font-semibold tracking-tight', selected ? 'text-brand-700' : 'text-fg')}>
              {device.patente}
            </span>
            <span className="text-[11px] text-fg-muted tabular-nums">
              {formatRelative(device.ultimoReporte)}
            </span>
          </div>
          <div className="text-xs text-fg-muted truncate mt-0.5 flex items-center gap-1.5">
            <VehicleIcon className="w-3 h-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{device.alias} · {device.vehiculoMarca} {device.vehiculoModelo}</span>
          </div>
          <div className="mt-1.5 flex items-center gap-3 text-[11px] text-fg-muted">
            <span className={cn('tabular-nums font-medium', device.estado === 'movimiento' && 'text-fg')}>
              {device.estado === 'movimiento' ? `${vel} km/h` : t(meta.label)}
            </span>
            <span className="flex items-center gap-1">
              <SignalBars level={device.senalGsm} />
            </span>
            {alarmas > 0 && (
              <span className="inline-flex items-center gap-0.5 text-status-error font-medium">
                <AlertTriangle className="w-3 h-3" /> {alarmas}
              </span>
            )}
            {device.conductorNombre && (
              <span className="ml-auto truncate max-w-[120px]" title={device.conductorNombre}>
                {device.conductorNombre.split(' ')[0]}
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}
